/* eslint-disable complexity */
import type { Customer } from '../../@types/Customer.js';
import { MiddlewarePaymentError } from '../../error/MiddlewarePaymentError.js';
import type { InteractionPrePaymentMiddlewareFn, InteractionSuccessProps, OnInteraction } from '../../flow/types.js';
import type { PreMiddlewareFn } from '../../payment/createPayment.js';
import { fetchMarketplaceBalance } from '../../utils/entry.js';
import { logger } from '../../utils/logger.js';
import type { Wager } from '../../wager/Wager.js';

const getProductsString = (payableItems: readonly Wager[]): string => {
  const products: string[] = [
    ...new Set(
      payableItems.reduce<string[]>(
        (productsCollection, wager) => [...productsCollection, ...wager.getProductsInBet()],
        []
      )
    ),
  ];

  if (products.length <= 1) {
    return products.join('');
  }

  return `${products.slice(0, -1).join(', ')} och ${products.at(-1)}`;
};

const convertPayableItemsToNumberOfGames = (payableItems: number): string => {
  switch (payableItems) {
    case 1:
      return 'ett';
    case 2:
      return 'två';
    case 3:
      return 'tre';
    case 4:
      return 'fyra';
    case 5:
      return 'fem';
    case 6:
      return 'sex';
    case 7:
      return 'sju';
    case 8:
      return 'åtta';
    case 9:
      return 'nio';
    case 10:
      return 'tio';
    case 11:
      return 'elva';
    case 12:
      return 'tolv';
    default:
      return `dina`;
  }
};

type PaymentConfirmInteraction = InteractionSuccessProps & {
  customMessage?: string;
  gamesMessages: string[];
  payableItemsCount: number;
  role?: Customer;
};

/**
 * Middleware that triggers a payment confirmation dialog.
 *
 * @param onInteraction The callback to trigger the interaction.
 * @param currencyFormatter Optional currency formatter function to format prices in the dialog messages
 * @returns The middleware function.
 */
export function paymentConfirmMw(
  onInteraction: OnInteraction<PaymentConfirmInteraction>,
  currencyFormatter?: (amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string
): PreMiddlewareFn<Wager> {
  const fn: ReturnType<InteractionPrePaymentMiddlewareFn<Wager>> =
    ({ paymentInfo, payment }) =>
    async (next) => {
      try {
        const payableItems = payment.getPayableItems();
        const ownRowsWagers = payableItems.filter((wager) => wager.hasOwnRows);
        const customerRole = payableItems[0]?.customer;

        let addonMessage;
        if (ownRowsWagers.length > 1) {
          addonMessage =
            'Notera att maxgränsen för spelet är uppnått. Spelet kommer att delas upp och hittas under Mina spel.';
        }

        const gamesMessages = [];
        const productsStr = getProductsString(payableItems);
        const price = payment.getTotalPrice();
        const payableItemsCount = payment.count();

        const gamesStr = convertPayableItemsToNumberOfGames(payableItemsCount);
        const formattedPrice = currencyFormatter ? currencyFormatter(price) : `${price} kr`;
        const titles = [`Vill du betala ${gamesStr} spel på ${productsStr} för ${formattedPrice}`];
        const hasMarketplaceWager = payableItems.some((wager) => wager.activatedDrawId);

        for (const [index, payable] of payableItems.entries()) {
          const gameNumber = index + 1;
          let role: Customer | undefined;
          let marketplaceDrawName;
          const { activatedDrawId } = payable;
          if (activatedDrawId) {
            role = payable.customer;

            const productId = payable.bets[0]?.product;
            const drawNumber = payable.bets[0]?.drawNumber;
            // eslint-disable-next-line no-await-in-loop
            const balanceResponse = await fetchMarketplaceBalance({
              activatedDrawId,
              product: productId,
              drawNumber: drawNumber,
              customer: role,
              shouldForceFetch: false,
            });
            marketplaceDrawName = balanceResponse?.name;
            marketplaceDrawName && payableItemsCount === 1 && titles.push(`åt ${marketplaceDrawName}`);
          }
          if (payableItemsCount > 1 && hasMarketplaceWager) {
            const name = activatedDrawId ? (marketplaceDrawName ?? `lagspel`) : `Eget saldo`;
            const formattedMarketPlacePrice = currencyFormatter
              ? currencyFormatter(payable.price)
              : `${payable.price} kr`;
            gamesMessages.push(`Spel ${gameNumber}: ${formattedMarketPlacePrice} (${name})`);
          }
          if (payableItemsCount === 1 && activatedDrawId) {
            gamesMessages.push('Kvarvarande pengar återbetalas till medlemmarna.');
          }
        }

        const paymentInfoData = paymentInfo.get();
        let paymentInfoMessage;
        if (paymentInfoData.length > 0) {
          const prePaymentInfo = paymentInfoData.filter((info) => info.type === 'PRE');
          paymentInfoMessage = prePaymentInfo.map((info) => info.message).join(' ');
        }
        const customMessage = addonMessage ?? paymentInfoMessage;

        await onInteraction({
          title: titles.join(' '),
          ...(customMessage && { customMessage }),
          payableItemsCount,
          gamesMessages,
          role: customerRole,
        });
        next();
      } catch (error) {
        if (error instanceof Error) {
          logger.debug('Payment confirmation aborted', { message: error.message, error });
        }
        const mwError = new MiddlewarePaymentError({ clientCode: 'PAYMENT_ABORTED' });
        next(mwError);
      }
    };

  return fn;
}
